import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('vett_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vett_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  signup: (email, password, accountType) => api.post('/auth/signup', { email, password, account_type: accountType }),
  me: () => api.get('/auth/me'),
  resendVerification: () => api.post('/auth/send-verification'),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  getVerificationStatus: () => api.get('/auth/verification-status'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  updateCookieConsent: (consent) => api.post('/auth/cookie-consent', consent),
  getCookieConsent: () => api.get('/auth/cookie-consent'),
};

// Employee
export const employeeAPI = {
  getProfile: () => api.get('/employee/profile'),
  updateProfile: (data) => api.put('/employee/profile', data),
  getRecords: () => api.get('/employee/records'),
  getRecord: (id) => api.get(`/employee/records/${id}`),
  acknowledgeRecord: (id) => api.post(`/employee/records/${id}/acknowledge`),
  getDashboardStats: () => api.get('/employee/dashboard-stats'),
  getQuickActions: () => api.get('/employee/quick-actions'),
  dismissQuickAction: (actionKey) => api.post(`/employee/quick-actions/${actionKey}/dismiss`),
  getActivity: () => api.get('/employee/activity'),
  getMessages: () => api.get('/employee/messages'),
  getMessage: (id) => api.get(`/employee/messages/${id}`),
  markAllMessagesRead: () => api.post('/employee/messages/mark-all-read'),
  getUnreadCount: () => api.get('/employee/messages/unread-count'),
  replyToMessage: (id, content) => api.post(`/employee/messages/${id}/reply?content=${encodeURIComponent(content)}`),
  getAnalytics: () => api.get('/analytics/employee'),
  getConnections: () => api.get('/employee/connections'),
  removeConnection: (id) => api.delete(`/employee/connections/${id}`),
  getVettScore: () => api.get('/employee/vett-score'),
  getVettTier: () => api.get('/employee/vett-tier'),
  triggerTierUpdate: () => api.post('/employee/trigger-tier-update'),
  // Reference Requests (Employee sends to external contacts)
  createReferenceRequest: (data) => api.post('/employee/references/request', data),
  getReferenceRequests: () => api.get('/employee/references'),
  cancelReferenceRequest: (id) => api.delete(`/employee/references/${id}`),
  resendReferenceRequest: (id) => api.post(`/employee/references/${id}/resend`),
  // Record Requests (Employee requests VETT records from past employers)
  createRecordRequest: (data) => api.post('/employee/record-requests', data),
  getRecordRequests: () => api.get('/employee/record-requests'),
  cancelRecordRequest: (id) => api.delete(`/employee/record-requests/${id}`),
  resendRecordRequest: (id) => api.post(`/employee/record-requests/${id}/resend`),
  // Company Profile (for viewing connected companies)
  getCompanyProfile: (employerId) => api.get(`/employee/company/${employerId}`),
  // Peer Reviews
  requestPeerReview: (data) => api.post('/employee/peer-reviews/request', data),
  getPendingPeerReviewRequests: () => api.get('/employee/peer-reviews/requests'),
  submitPeerReview: (requestId, data) => api.post(`/employee/peer-reviews/submit/${requestId}`, data),
  getReceivedPeerReviews: () => api.get('/employee/peer-reviews'),
  getGivenPeerReviews: () => api.get('/employee/peer-reviews/given'),
  getSentPeerReviewRequests: () => api.get('/employee/peer-reviews/sent'),
  cancelPeerReviewRequest: (id) => api.delete(`/employee/peer-reviews/request/${id}`),
  // Profile Visibility
  getProfileVisibility: () => api.get('/employee/profile/visibility'),
  updateProfileVisibility: (visibility) => api.put('/employee/profile/visibility', { visibility }),
  // Access Requests (from employers)
  getAccessRequests: () => api.get('/employee/access-requests'),
  getAllAccessRequests: () => api.get('/employee/access-requests/all'),
  respondToAccessRequest: (requestId, action) => api.post(`/employee/access-requests/${requestId}/respond?action=${action}`),
  resetAccessPermissions: () => api.post('/employee/access-requests/reset'),
  getAccessApprovals: () => api.get('/employee/access-approvals'),
  revokeAccessApproval: (employerId) => api.delete(`/employee/access-approvals/${employerId}`),
  // FCRA Data File Export
  getFcraDataFile: () => api.get('/employee/export/fcra-file'),
};

// Public APIs (no auth required)
export const publicAPI = {
  getReferenceRequest: (token) => api.get(`/public/reference/${token}`),
  submitReference: (token, data) => api.post(`/public/reference/${token}`, data),
  declineReference: (token) => api.post(`/public/reference/${token}/decline`),
  // Record Requests
  getRecordRequest: (token) => api.get(`/public/record-request/${token}`),
  // Company Profile
  getCompanyProfile: (slug) => api.get(`/company/${slug}`),
};

// Employer
export const employerAPI = {
  getProfile: () => api.get('/employer/profile'),
  updateProfile: (data) => api.put('/employer/profile', data),
  getDashboardStats: () => api.get('/employer/dashboard-stats'),
  getQuickActions: () => api.get('/employer/quick-actions'),
  dismissQuickAction: (actionKey) => api.post(`/employer/quick-actions/${actionKey}/dismiss`),
  getActivity: () => api.get('/employer/activity'),
  getTeamComparison: () => api.get('/employer/team-comparison'),
  searchTalent: (params) => api.get('/employer/search', { params }),
  searchByVettId: (vettId) => api.get('/employer/search', { params: { vett_id: vettId } }),
  getTalentProfile: (id) => api.get(`/employer/talent/${id}`),
  exportTalentPdf: (id) => api.get(`/employer/talent/${id}/pdf`, { responseType: 'blob' }),
  sendReachOut: (employeeId, content) => api.post(`/employer/reach-out?employee_id=${employeeId}&content=${encodeURIComponent(content)}`),
  saveCandidate: (employeeId) => api.post(`/employer/save-candidate?employee_id=${employeeId}`),
  getSavedCandidates: () => api.get('/employer/saved-candidates'),
  getRecords: (status) => api.get('/employer/records', { params: { status } }),
  getRecord: (id) => api.get(`/employer/records/${id}`),
  createRecord: (data) => api.post('/employer/records', data),
  updateRecord: (id, data) => api.put(`/employer/records/${id}`, data),
  submitRecord: (id) => api.post(`/employer/records/${id}/submit`),
  archiveRecord: (id) => api.post(`/employer/records/${id}/archive`),
  restoreRecord: (id) => api.post(`/employer/records/${id}/restore`),
  deleteRecord: (id) => api.delete(`/employer/records/${id}`),
  getMessages: () => api.get('/employer/messages'),
  getMessage: (id) => api.get(`/employer/messages/${id}`),
  getUnreadCount: () => api.get('/employer/messages/unread-count'),
  archiveMessage: (id) => api.post(`/employer/messages/${id}/archive`),
  unarchiveMessage: (id) => api.post(`/employer/messages/${id}/unarchive`),
  replyToMessage: (id, content) => api.post(`/employer/messages/${id}/reply?content=${encodeURIComponent(content)}`),
  getAnalytics: () => api.get('/analytics/employer'),
  getEmployees: () => api.get('/employer/employees'),
  getPerformanceAnalytics: (dateRange) => api.get('/analytics/employer/performance', { params: { date_range: dateRange } }),
  // Staff/Employee Invitations
  getStaff: () => api.get('/employer/staff'),
  inviteEmployee: (data) => api.post('/employer/staff/invite', data),
  updateStaffStatus: (id, status) => api.put(`/employer/staff/${id}/status?status=${status}`),
  resendInvitation: (id) => api.post(`/employer/staff/${id}/resend`),
  getCompanyInfo: () => api.get('/employer/company'),
  // Reference Requests
  createReferenceRequest: (data) => api.post('/employer/references/request', data),
  getReferenceRequests: (params) => api.get('/employer/references', { params }),
  getReferenceDetail: (id) => api.get(`/employer/references/${id}`),
  getReferenceSummary: (employeeId) => api.get(`/employer/references/summary/${employeeId}`),
  resendReferenceRequest: (id) => api.post(`/employer/references/${id}/resend`),
  cancelReferenceRequest: (id) => api.delete(`/employer/references/${id}`),
  // Record Requests (incoming from employees)
  getRecordRequests: (params) => api.get('/employer/record-requests', { params }),
  acceptRecordRequest: (token) => api.post(`/public/record-request/${token}/accept`),
  declineRecordRequest: (token, reason) => api.post(`/public/record-request/${token}/decline`, null, { params: { reason } }),
  // Candidate Pipeline
  getPipelineStages: () => api.get('/employer/pipeline/stages'),
  updatePipelineStages: (stages) => api.put('/employer/pipeline/stages', stages),
  getPipelineCandidates: () => api.get('/employer/pipeline/candidates'),
  addToPipeline: (data) => api.post('/employer/pipeline/candidates', data),
  updatePipelineCandidate: (id, data) => api.put(`/employer/pipeline/candidates/${id}`, data),
  removeFromPipeline: (id) => api.delete(`/employer/pipeline/candidates/${id}`),
  bulkMoveCandidates: (data) => api.post('/employer/pipeline/candidates/bulk-move', data),
  // Access Requests (for employees with selective visibility)
  requestFullAccess: (employeeId) => api.post(`/employer/request-access/${employeeId}`),
  checkAccessStatus: (employeeId) => api.get(`/employer/access-status/${employeeId}`),
  // Company Tier
  getCompanyTier: () => api.get('/employer/company-tier'),
  recalculateCompanyTier: () => api.post('/employer/company-tier/recalculate'),
  // Candidate Comparison
  compareCandidates: (employeeIds) => api.post('/employer/compare-candidates', { employee_ids: employeeIds }),
  updateCandidateNotes: (employeeId, notes) => api.put(`/employer/compare-candidates/${employeeId}/notes`, { notes }),
  // ATS Integration
  getATSWebhookStatus: () => api.get('/integrations/ats/webhook/status'),
  configureATSWebhook: (data) => api.post('/integrations/ats/webhook/configure', data),
  removeATSWebhook: () => api.delete('/integrations/ats/webhook'),
  getATSLogs: (limit = 50) => api.get('/integrations/ats/logs', { params: { limit } }),
  pushToATS: (employeeId, consentType = 'profile_share') => api.post('/integrations/ats/push', { employee_id: employeeId, consent_type: consentType }),
};

// Flags
export const flagsAPI = {
  create: (data) => api.post('/flags', data),
  get: (id) => api.get(`/flags/${id}`),
  addMessage: (id, message) => api.post(`/flags/${id}/message`, { flag_id: id, message }),
  resolve: (id) => api.post(`/flags/${id}/resolve`),
};

// Reviews
export const reviewsAPI = {
  // Employer
  create: (data) => api.post('/reviews', data),
  getEmployerReviews: (status) => api.get('/reviews/employer', { params: { status } }),
  getScheduledReviews: () => api.get('/employer/reviews/scheduled'),
  getCalendarReviews: () => api.get('/reviews/calendar'),
  getReviewRequests: () => api.get('/employer/review-requests'),
  acceptRequest: (id) => api.post(`/review-requests/${id}/accept`),
  declineRequest: (id, reason) => api.post(`/review-requests/${id}/decline?reason=${encodeURIComponent(reason || '')}`),
  
  // Scheduling
  scheduleReview: (data) => api.post('/employer/reviews/schedule', data),
  getScheduledReview: (id) => api.get(`/employer/reviews/scheduled/${id}`),
  updateScheduledReview: (id, data) => api.put(`/employer/reviews/scheduled/${id}`, data),
  cancelScheduledReview: (id) => api.delete(`/employer/reviews/scheduled/${id}`),
  completeScheduledReview: (id) => api.post(`/employer/reviews/scheduled/${id}/complete`),
  
  // Employee
  getEmployeeReviews: () => api.get('/reviews/employee'),
  getEmployeeScheduledReviews: () => api.get('/reviews/employee/scheduled'),
  getReview: (id) => api.get(`/reviews/${id}`),
  addSelfAssessment: (id, selfAssessment) => api.post(`/reviews/${id}/self-assessment`, { self_assessment: selfAssessment }),
  acknowledgeReview: (id) => api.post(`/reviews/${id}/acknowledge`),
  flagReview: (data) => api.post('/reviews/flag', data),
  requestReview: (data) => api.post('/review-requests', data),
  getMyRequests: () => api.get('/review-requests/employee'),
};

// Payments
export const paymentsAPI = {
  createCheckout: (planId, originUrl) => api.post('/payments/checkout', { plan_id: planId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getBillingPortal: () => api.post('/payments/billing-portal'),
  getSubscriptionInfo: () => api.get('/payments/subscription-info'),
  getBillingDetails: () => api.get('/payments/billing/details'),
  cancelSubscription: () => api.post('/payments/billing/cancel-subscription'),
  reactivateSubscription: () => api.post('/payments/billing/reactivate-subscription'),
};

// Account Management
export const accountAPI = {
  pauseAccount: () => api.post('/account/pause'),
  deleteAccount: (confirmText) => api.post('/account/delete', null, { params: { confirm_text: confirmText } }),
};

// Referral Program
export const referralAPI = {
  getInfo: () => api.get('/referral/info'),
  validateCode: (code) => api.get(`/referral/validate/${code}`),
  applyCode: (code) => api.post('/referral/apply', null, { params: { code } }),
  getLeaderboard: () => api.get('/referral/leaderboard'),
};

// Notification Settings
export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (settings) => api.put('/notifications/settings', settings),
};

// Admin API
export const adminAPI = {
  // Command Center
  getCommandCenter: () => api.get('/admin/command-center'),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  getUserFullProfile: (userId) => api.get(`/admin/users/${userId}/full-profile`),
  suspendUser: (userId, reason) => api.post(`/admin/users/${userId}/suspend`, null, { params: { reason } }),
  unsuspendUser: (userId) => api.post(`/admin/users/${userId}/unsuspend`),
  flagUser: (userId, reason) => api.post(`/admin/users/${userId}/flag`, null, { params: { reason } }),
  unflagUser: (userId) => api.post(`/admin/users/${userId}/unflag`),
  verifyUserEmail: (userId) => api.post(`/admin/users/${userId}/verify-email`),
  sendMessage: (userId, subject, content) => api.post(`/admin/users/${userId}/send-message`, null, { params: { subject, content } }),
  triggerPasswordReset: (userId) => api.post(`/admin/users/${userId}/trigger-password-reset`),
  exportUserData: (userId) => api.post(`/admin/users/${userId}/export-data`),
  updateSubscription: (userId, tier, trialDays) => api.post(`/admin/users/${userId}/subscription`, { subscription_tier: tier, trial_days: trialDays }),
  
  // Records
  getRecords: (params) => api.get('/admin/records', { params }),
  getRecordDetail: (recordId) => api.get(`/admin/records/${recordId}`),
  getDisputedRecords: (params) => api.get('/admin/records/disputed', { params }),
  
  // Email Logs
  getEmailLogs: (params) => api.get('/admin/email-logs', { params }),
  getEmailStats: () => api.get('/admin/email-logs/stats'),
  
  // Flagged Accounts
  getFlaggedAccounts: (params) => api.get('/admin/flagged-accounts', { params }),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Analytics (Phase 2)
  getAnalytics: (period) => api.get('/admin/analytics', { params: { period } }),
  
  // Financial Operations (Stripe Webhooks)
  getFinancialOverview: () => api.get('/admin/financial/overview'),
  getPastDueAccounts: (params) => api.get('/admin/financial/past-due', { params }),
  retryPayment: (userId) => api.post(`/admin/financial/retry-payment/${userId}`),
  getCancelledAccounts: (params) => api.get('/admin/financial/cancelled-accounts', { params }),
  getInvoiceHistory: (params) => api.get('/admin/financial/invoices', { params }),
  getWebhookLogs: (params) => api.get('/admin/financial/webhook-logs', { params }),
  
  // Referrals (Phase 3)
  getReferrals: (params) => api.get('/admin/referrals', { params }),
  markReferralRewarded: (referralId) => api.post(`/admin/referrals/${referralId}/mark-rewarded`),
  
  // Admin Logs (Phase 3)
  getLogs: (params) => api.get('/admin/logs', { params }),
  
  // Settings (Phase 3)
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),
  
  // Records Under Review (Tier System)
  getRecordsUnderReview: () => api.get('/admin/records-under-review'),
  clearRecordReview: (recordId) => api.post(`/admin/records-under-review/${recordId}/clear`),
  markRecordReviewedNoAction: (recordId) => api.post(`/admin/records-under-review/${recordId}/reviewed-no-action`),
  runTierCalculation: () => api.post('/admin/run-tier-calculation'),
  
  // Hard delete user
  hardDeleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // GDPR Data Centre
  getGDPRSubjects: (params) => api.get('/admin/gdpr/subjects', { params }),
  getGDPRSubjectData: (subjectId) => api.get(`/admin/gdpr/subjects/${subjectId}`),
  exportGDPRSubjectData: (subjectId) => api.post(`/admin/gdpr/export/${subjectId}`),
  scheduleGDPRDeletion: (subjectId, data) => api.post(`/admin/gdpr/schedule-deletion/${subjectId}`, data),
  cancelGDPRDeletion: (subjectId) => api.post(`/admin/gdpr/cancel-deletion/${subjectId}`),
  executeGDPRDeletion: (subjectId) => api.post(`/admin/gdpr/execute-deletion/${subjectId}`),
  getGDPRRequests: (params) => api.get('/admin/gdpr/requests', { params }),
  getGDPRAuditLog: (params) => api.get('/admin/gdpr/audit-log', { params }),
  
  // God Mode / Impersonation
  godModeSearch: (query) => api.get('/admin/god-mode/search', { params: { query } }),
  godModeGetUser: (userId) => api.get(`/admin/god-mode/user/${userId}`),
  impersonateUser: (userId) => api.post(`/admin/impersonate/${userId}`),
  getFcraAudit: (userId) => api.get(`/admin/god-mode/fcra-audit/${userId}`),
  getImpersonationLogs: (limit = 50) => api.get('/admin/god-mode/impersonation-logs', { params: { limit } }),
  
  // Operations Suite - Advanced Metrics
  getDeepFinancialMetrics: () => api.get('/admin/financial/deep-metrics'),
  getGeographicDistribution: () => api.get('/admin/analytics/geographic'),
  getActivityFirehose: (limit = 50) => api.get('/admin/activity-firehose', { params: { limit } }),
};

// Utility
export const utilityAPI = {
  getIndustries: () => api.get('/industries'),
};

export default api;
